import { FormControlLabel, Switch, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import { selectTypoCheckEnabled, toggleTypoCheck } from '@src/store/slices/typo_check';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface TypoCheckSwitchProps {
  isFocusMode: boolean;
  isAuthEnabled: boolean;
  isAuthenticated: boolean;
}

export const TypoCheckSwitch = memo(
  ({ isFocusMode, isAuthEnabled, isAuthenticated }: TypoCheckSwitchProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isTypoCheckEnabled = useSelector(selectTypoCheckEnabled);
    const isTypoFixEnabled = useFeatureFlag(FeatureFlag.TYPO_FIX);

    if (!isTypoFixEnabled || !isAuthEnabled || !isAuthenticated) {
      return null;
    }

    return (
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={isTypoCheckEnabled}
            onChange={() => {
              dispatch(toggleTypoCheck());
            }}
            inputProps={{
              'aria-label': isTypoCheckEnabled
                ? t('navigation.typoCheckOn')
                : t('navigation.typoCheckOff'),
            }}
            sx={(theme) => ({
              '& .MuiSwitch-switchBase': {
                color: isFocusMode
                  ? 'rgba(255, 255, 255, 0.75)'
                  : alpha(theme.palette.primary.main, 0.75),
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: isFocusMode ? theme.palette.common.white : theme.palette.primary.main,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: isFocusMode
                  ? 'rgba(255, 255, 255, 0.85)'
                  : alpha(theme.palette.primary.main, 0.85),
                opacity: 1,
              },
              '& .MuiSwitch-track': {
                backgroundColor: isFocusMode
                  ? 'rgba(255, 255, 255, 0.35)'
                  : alpha(theme.palette.text.secondary, 0.35),
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                boxShadow: 'none',
              },
            })}
          />
        }
        label={
          <Typography variant="caption" component="span" sx={{ color: 'inherit' }}>
            {isTypoCheckEnabled ? t('navigation.typoCheckOn') : t('navigation.typoCheckOff')}
          </Typography>
        }
        sx={{
          m: 0,
          '.MuiSwitch-root': {
            mr: 0.5,
          },
          '.MuiFormControlLabel-label': {
            fontSize: '0.7rem',
          },
        }}
      />
    );
  }
);
